import { Box, Card, Container, Table, TableBody, TableContainer, TableHead, TableRow } from "@mui/material";
import { memo, useCallback, useEffect } from "react";
import axios, { endpoints } from 'src/utils/axios';
import { useSettingsContext } from "src/components/settings";
import { EldEventLine } from "src/models";
import useModelData from "src/hooks/use-model-data";
import Iconify from "src/components/iconify/iconify";
import { DisplayPageData } from "src/components/display-page-data";

import PageHeader from "src/components/page-header";
import NoneFound from "src/components/none-found";
import { useSort } from "src/hooks/use-sort";
import { SortableHeader } from "src/components/sortable-header";
import { TruxTableCell } from "src/components/trux-table-cell";
import { useRouter } from "src/routes/hooks";
import { ColoredIcon } from "src/components/colored-icon";
import { useSearch } from "src/hooks/use-search";
import { SearchCard } from "src/components/search-card";


type EldUnidentifiedLogsProps = {
  eldUnidentifiedLogs?: EldEventLine[],
  onEldUnidentifiedLogSelected: (a: EldEventLine) => void
}

const EldUnidentifiedLogs = memo(({ eldUnidentifiedLogs, onEldUnidentifiedLogSelected }: EldUnidentifiedLogsProps) => {
  const { sortedList, sortData } = useSort(eldUnidentifiedLogs);

  const {
    searchText,
    setSearchText,
    filteredList,
  } = useSearch(sortedList, ["eventType", "locationSource", "location"]);

  if (!sortedList) {
    return null;
  }

  return <div>
    <SearchCard searchText={searchText} setSearchText={setSearchText} searchingFor="EldUnidentifiedLogs" />

    <Card>
      <TableContainer sx={{ position: 'relative', overflow: 'scroll' }}>
        <Table>
          <TableHead>
            <TableRow>
              <SortableHeader sortKey="eventTime" sortData={sortData} >eventTime</SortableHeader>
              <SortableHeader sortKey="eventDate" sortData={sortData} >eventDate</SortableHeader>
              <SortableHeader sortKey="eventType" sortData={sortData} >eventType</SortableHeader>
              <SortableHeader sortKey="numberId" sortData={sortData} >numberId</SortableHeader>
              <SortableHeader sortKey="engineHours" sortData={sortData} >engineHours</SortableHeader>
              <SortableHeader sortKey="origin" sortData={sortData} >origin</SortableHeader>
              <SortableHeader sortKey="odometer" sortData={sortData} >odometer</SortableHeader>
              <SortableHeader sortKey="locationSource" sortData={sortData} >locationSource</SortableHeader>
              <SortableHeader sortKey="location" sortData={sortData} >location</SortableHeader>
            </TableRow>
          </TableHead>


          <TableBody>
            {filteredList.map((eldUnidentifiedLog, index) =>
            (<TableRow
              key={index}
              hover
              onClick={() => onEldUnidentifiedLogSelected(eldUnidentifiedLog)}
              sx={{ cursor: "pointer" }}
            >

              <TruxTableCell>{eldUnidentifiedLog.eventTime}</TruxTableCell>
              <TruxTableCell>{eldUnidentifiedLog.eventDate}</TruxTableCell>
              <TruxTableCell>{eldUnidentifiedLog.eventType}</TruxTableCell>
              <TruxTableCell>{eldUnidentifiedLog.numberId}</TruxTableCell>
              <TruxTableCell>{eldUnidentifiedLog.engineHours}</TruxTableCell>
              <TruxTableCell>{eldUnidentifiedLog.origin}</TruxTableCell>
              <TruxTableCell>{eldUnidentifiedLog.odometer}</TruxTableCell>
              <TruxTableCell>{eldUnidentifiedLog.locationSource}</TruxTableCell>
              <TruxTableCell>{eldUnidentifiedLog.location}</TruxTableCell>

            </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Card>
  </div >;
});

type PageModel = {
  eldUnidentifiedLogs?: EldEventLine[],
}

export default function Page() {
  const settings = useSettingsContext();
  const router = useRouter();
  const dataLoader = useCallback(() => ({
    eldUnidentifiedLogs: axios.get(endpoints.eldUnidentifiedLogs.findAll).then(response => response.data),
  }), []);
  const {
    data,
    updateData,
    setData,
    error,
    loading,
  } = useModelData<PageModel>(dataLoader);

  useEffect(() => {
    if (!loading && error) {
      console.log(error);
      alert(error);
    }
  }, [loading, error]);


  const onEldUnidentifiedLogSelected = useCallback((a: EldEventLine) => {
    console.log("clicked", a);
  }, []);

  return (
    <Container maxWidth={settings.themeStretch ? false : 'lg'}>
      <DisplayPageData loading={loading} error={error} data={data}>
        {data && data?.eldUnidentifiedLogs?.length !== 0 &&
          <PageHeader title="Unidentified Logs" />
        }

        {data && data?.eldUnidentifiedLogs?.length === 0 &&
          <NoneFound title={
            <div>
              <Box textAlign="center">
                <ColoredIcon color="success">
                  <Iconify icon="solar:check-circle-bold" width={44} />
                </ColoredIcon>
              </Box>
              No Unidentified Logs Found
            </div>
          } message={<>Unidentified Logs are used to track activity on ELDs when no Driver has been logged in.<br /> All unidentified events should be assigned to drivers.</>} />
        }

        {data && data?.eldUnidentifiedLogs?.length !== 0 && <EldUnidentifiedLogs eldUnidentifiedLogs={data.eldUnidentifiedLogs} onEldUnidentifiedLogSelected={onEldUnidentifiedLogSelected} />}
      </DisplayPageData>
    </Container >
  );
}
